/* InputPowerButtonControlRenderer.js */

"use strict";
const _ = require("lodash");

class InputPowerButtonControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("input-powerbutton-control");
        this._rootElem.tabIndex = 0;
        if (!_.isEmpty(model.toolTip)) {
            this._rootElem.setAttribute("title", model.toolTip);
        }

        this._powerButtonBlock = document.createElement("div");
        this._powerButtonBlock.classList.add("power-button-block");
        this._rootElem.appendChild(this._powerButtonBlock);

        this._powerButton = document.createElement("input");
        this._powerButton.setAttribute("type", "checkbox");
        this._powerButton.setAttribute("value", "None");
        let id = window.performance.now();
        this._powerButton.setAttribute("id", id);
        this._powerButtonBlock.appendChild(this._powerButton);

        let buttonCenter = document.createElement("label");
        buttonCenter.setAttribute("for", id);
        buttonCenter.classList.add("power-indicator");
        this._powerButtonBlock.appendChild(buttonCenter);

        let powerOn = document.createElement("base-icon-gl");
        powerOn.setAttribute("data-icon", "power.button.on");
        powerOn.classList.add("power-on");
        buttonCenter.appendChild(powerOn);

        let powerOff = document.createElement("base-icon-gl");
        powerOff.setAttribute("data-icon", "power.button.off");
        powerOff.classList.add("power-off");
        buttonCenter.appendChild(powerOff);

        this._label = document.createElement("div");
        this._label.classList.add("button-label");

        this._rootElem.component = this._model;

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            resolve();
        });
        return prom;
    }

    // ["Disabled", "Value", "Visible", "Invalid", "DarkTheme", "Theme", "Label", "LabelShown", "Focus", "Blur", "UserValue", "UserFocus", "UserBlur", "UserKeyDown"]

    _registerListeners() {
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("Invalid", (this._onInvalid = this._onInvalid.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("LabelShown", (this._onLabelShown = this._onLabelShown.bind(this)));
        this._model.on("Label", (this._onLabel = this._onLabel.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on("SetAttribute", (this._onSetAttribute = this._onSetAttribute.bind(this)));
        this._model.on("ToolTip", (this._onToolTip = this._onToolTip.bind(this)));
        this._powerButton.addEventListener(
            "click",
            (this._onClickEvent = this._onClickEvent.bind(this))
        );
        this._rootElem.addEventListener(
            "focus",
            (this._onFocusEvent = this._onFocusEvent.bind(this))
        );
        this._rootElem.addEventListener("blur", (this._onBlurEvent = this._onBlurEvent.bind(this)));
        this._rootElem.addEventListener(
            "keydown",
            (this._onKeyDownEvent = this._onKeyDownEvent.bind(this))
        );
    }
    _unregisterListeners() {
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("Invalid", this._onInvalid);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("LabelShown", this._onLabelShown);
        this._model.removeListener("Label", this._onLabel);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("SetAttribute", this._onSetAttribute);
        this._model.removeListener("ToolTip", this._onToolTip);
        this._rootElem.removeEventListener("click", this._onClickEvent);
        this._rootElem.removeEventListener("focus", this._onFocusEvent);
        this._rootElem.removeEventListener("blur", this._onBlurEvent);
        this._rootElem.removeEventListener("keydown", this._onKeyDownEvent);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            if (this._model.isLabelShown()) {
                this._rootElem.appendChild(this._label);
            }
            this._setDisabled(this._model.disabled);
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._setInvalid(this._model.invalid);
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);
            this._setLabel(this._model.label);
            this._setLabelShown(this._model.labelShown);
            resolve();
        });
        return prom;
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal) {
        this._setDisabled(newVal);
    }

    /**
     * Fired when the shown state of the controls label has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onLabelShown(newVal) {
        this._setLabelShown(newVal);
    }

    /**
     * Fired when the text of the controls label has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onLabel(newVal) {
        this._setLabel(newVal);
    }

    /**
     * Fired when the invalid state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal) {
        this._setInvalid(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {Boolean}
     * @param oldVal {Boolean}
     * @private
     */
    _onValue(newVal /*, oldVal*/) {
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal) {
        this._setVisible(newVal);
    }

    /**
     * Fired when the darkTheme has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal) {
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when the theme has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onTheme(newVal, oldVal) {
        this._setTheme(newVal, oldVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus() {
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur() {
        this._setFocus(false);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue) {
        this._setAttribute(attrName, attrValue);
    }

    /**
     * Handle the tool tip event.
     * @param toolTip
     * @private
     */
    _onToolTip(toolTip) {
        this._setToolTip(toolTip);
    }

    /**
     * If the control is not disabled, toggle the checked state
     * @param event {Event}
     * @private
     */
    _onClickEvent(event) {
        let oldVal = _.cloneDeep(this._model.value);
        if (this._model._disabled) {
            event.stopImmediatePropagation();
        } else {
            let newVal = !this._model.value;
            this._model.setValue(newVal, true);
            this._model._triggerUserValue(newVal, oldVal, event);
        }
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event) {
        this._model._triggerUserFocus(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event) {
        this._model._triggerUserBlur(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event) {
        this._model._triggerUserKeyDown(event);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal) {
        if (newVal === true) {
            this._powerButton.setAttribute("disabled", true);
            this._powerButton.tabIndex = -1;
            this._powerButtonBlock.classList.add("disabled");
        } else {
            this._powerButton.removeAttribute("disabled");
            this._powerButton.tabIndex = 0;
            this._powerButtonBlock.classList.remove("disabled");
        }
    }

    /**
     * Add/Remove the controls label
     * @param newVal {Boolean}
     * @private
     */
    _setLabelShown(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("label-shown");
            this._setLabel(this._model.label);
        } else {
            this._rootElem.classList.remove("label-shown");
            this._label.innerHTML = "";
        }
    }

    /**
     * Updates the controls label text
     * @param newVal {String}
     * @private
     */
    _setLabel(newVal) {
        this._label.innerHTML = newVal;
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal) {
        if (newVal === true) {
            this._powerButtonBlock.classList.add("invalid");
        } else {
            this._powerButtonBlock.classList.remove("invalid");
        }
    }

    /**
     * Toggle the value of the control
     * @param newVal {String}
     * @private
     */
    _setValue(newVal) {
        if (newVal) {
            this._rootElem.classList.add("checked");
        } else {
            this._rootElem.classList.remove("checked");
        }
        this._powerButton.checked = newVal;
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal) {
        if (newVal === true) {
            this._rootElem.classList.remove("hidden");
        } else {
            this._rootElem.classList.add("hidden");
        }
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("dark");
        } else {
            this._rootElem.classList.remove("dark");
        }
    }

    /**
     * Set the theme name
     * @param newVal {String}
     * @private
     */
    _setTheme(newVal, oldVal) {
        if (oldVal) {
            this._rootElem.classList.remove(oldVal);
        }
        if (newVal) {
            this._rootElem.classList.add(newVal);
        }
    }

    /**
     * Set or remove focus on the control
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal) {
        if (newVal === true) {
            this._rootElem.focus();
        } else {
            this._rootElem.blur();
        }
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue) {
        if (!_.isUndefined(attrValue)) {
            this._rootElem.setAttribute(attrName, attrValue);
        } else {
            this._rootElem.removeAttribute(attrName);
        }
    }

    /**
     * Set the value for the toolTip
     * @param {string} toolTip The toolTip
     * @private
     */
    _setToolTip(toolTip) {
        this._rootElem.setAttribute("title", toolTip);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = InputPowerButtonControlRenderer;
